import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CreditRateSettingsView, CreditRateTableView, ServiceUsageRateKind } from '../api.js';
import { DataTable } from '../components/DataTable.js';
import { describeError } from '../errors.js';
import type { AdminHttpClient } from '../http.js';
import type { ToastMessage } from '../types.js';
import {
  diffRateSettings,
  draftFromRateSettings,
  summarizeChanges,
  validateUsdPerCredit,
  type RateSettingsDraft,
  type ServiceRateDraft,
} from './rates/rate-settings-form.js';

const UNITS: Record<ServiceUsageRateKind, ServiceRateDraft['unitName']> = {
  web_search: 'request',
  image_input: 'image',
  image_output: 'image',
  video_output: 'second',
};
const KINDS = Object.keys(UNITS) as ServiceUsageRateKind[];

function emptyRow(): ServiceRateDraft {
  return {
    kind: 'web_search',
    rateKey: '',
    unitName: 'request',
    costMicroUsdPerUnit: '0',
    marginMultiplier: '1',
    provisional: true,
    sourceUrl: '',
    verifiedAt: new Date().toISOString(),
    note: '',
  };
}

export function RatesScreen({
  http,
  onToast,
  onOpenModels,
}: {
  http: AdminHttpClient;
  onToast: (toast: ToastMessage) => void;
  onOpenModels: () => void;
}) {
  const { t } = useTranslation();
  const translate = useCallback(
    (key: string, options?: Record<string, unknown>): string =>
      options ? t(key, options) : t(key),
    [t],
  );
  const [loaded, setLoaded] = useState<CreditRateSettingsView>();
  const [draft, setDraft] = useState<RateSettingsDraft>();
  const [current, setCurrent] = useState<CreditRateTableView>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const load = useCallback(async () => {
    setError(undefined);
    try {
      const [nextSettings, nextCurrent] = await Promise.all([
        http.getRateSettings(),
        http.getCurrentRates(),
      ]);
      setLoaded(nextSettings);
      setDraft(draftFromRateSettings(nextSettings));
      setCurrent(nextCurrent);
    } catch (loadError) {
      const message = describeError(loadError, translate);
      setError(message);
      onToast({ tone: 'error', message });
    }
  }, [http, onToast, translate]);

  useEffect(() => {
    void load();
  }, [load]);

  const draftState = useMemo(() => {
    if (!loaded || !draft) return undefined;
    const keys = draft.serviceRates.map((row) => `${row.kind}:${row.rateKey}`);
    if (new Set(keys).size !== keys.length) {
      return { error: t('rates.duplicateRateKey') };
    }
    try {
      const diff = diffRateSettings(loaded, draft);
      return { diff, summary: summarizeChanges(diff) };
    } catch (validationError) {
      const [code, field] =
        validationError instanceof Error ? validationError.message.split(':', 2) : [];
      return {
        error: t('rates.validationError', {
          message: code ? t(`errors.codes.${code}`) : t('errors.requestFailed'),
          field: field ?? t('common.none'),
        }),
      };
    }
  }, [draft, loaded, t]);

  const hasChanges = Boolean(
    draftState?.diff &&
    (draftState.diff.serviceRates.length > 0 ||
      draftState.diff.removeServiceRates.length > 0 ||
      draftState.diff.usdPerCredit)
  );
  const hasDrift = Boolean(
    loaded && (loaded.drift.serviceRates.length > 0 || loaded.drift.usdPerCredit)
  );

  function updateRow(row: ServiceRateDraft, patch: Partial<ServiceRateDraft>) {
    setDraft((value) =>
      value
        ? {
            ...value,
            serviceRates: value.serviceRates.map((candidate) =>
              candidate === row ? { ...candidate, ...patch } : candidate
            ),
          }
        : value
    );
  }

  function removeRow(row: ServiceRateDraft) {
    setDraft((value) =>
      value
        ? { ...value, serviceRates: value.serviceRates.filter((candidate) => candidate !== row) }
        : value
    );
  }

  async function adoptPublished() {
    if (!window.confirm(t('rates.confirmAdopt'))) return;
    setBusy(true);
    try {
      const nextSettings = await http.adoptPublishedRateSettings();
      setLoaded(nextSettings);
      setDraft(draftFromRateSettings(nextSettings));
      onToast({ tone: 'success', message: t('rates.adopted') });
    } catch (adoptError) {
      const message = describeError(adoptError, translate);
      setError(message);
      onToast({ tone: 'error', message });
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!loaded || !draftState?.diff || !draftState.summary || !hasChanges) return;
    if (draftState.summary.usdPerCreditChanged && !window.confirm(t('rates.confirmUsdPerCredit')))
      return;
    if (
      !window.confirm(
        t('rates.confirmPublish', {
          serviceRateCount: draftState.summary.serviceRateCount,
          usdPerCredit: draftState.summary.usdPerCreditChanged
            ? t('common.changed')
            : t('common.none'),
        })
      )
    )
      return;

    setBusy(true);
    setError(undefined);
    try {
      await http.putRateSettings({
        expectedPublishedVersion: loaded.publishedVersion ?? '',
        ...(draftState.diff.serviceRates.length > 0
          ? { serviceRates: draftState.diff.serviceRates }
          : {}),
        ...(draftState.diff.removeServiceRates.length > 0
          ? { removeServiceRates: draftState.diff.removeServiceRates }
          : {}),
        ...(draftState.diff.usdPerCredit ? { usdPerCredit: draftState.diff.usdPerCredit } : {}),
      });
      const [nextSettings, nextCurrent] = await Promise.all([
        http.getRateSettings(),
        http.getCurrentRates(),
      ]);
      setLoaded(nextSettings);
      setDraft(draftFromRateSettings(nextSettings));
      setCurrent(nextCurrent);
      onToast({ tone: 'success', message: t('rates.savedWithCacheDelay') });
    } catch (saveError) {
      const message = describeError(saveError, translate);
      setError(message);
      onToast({ tone: 'error', message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="content-stack">
      <p className="screen-help">{t('screenHelp.rates')}</p>
      {error ? (
        <div className="error" role="alert">
          {error}
        </div>
      ) : null}
      {hasDrift ? (
        <section className="warning rate-drift-banner" role="alert">
          <div>
            <strong>{t('rates.driftTitle')}</strong>
            <p>{t('rates.driftBody', { count: loaded?.drift.serviceRates.length ?? 0 })}</p>
          </div>
          <button
            type="button"
            className="secondary"
            disabled={busy}
            onClick={() => void adoptPublished()}
          >
            {t('rates.adoptPublished')}
          </button>
        </section>
      ) : null}

      <section className="panel content-stack">
        <div className="rate-section-heading">
          <div>
            <h2>{t('rates.serviceRates')}</h2>
            <p className="muted">{t('rates.serviceRatesHelp')}</p>
          </div>
          <button
            type="button"
            className="secondary"
            onClick={() =>
              setDraft((value) =>
                value ? { ...value, serviceRates: [...value.serviceRates, emptyRow()] } : value
              )
            }
            disabled={!draft || busy}
          >
            {t('rates.addRow')}
          </button>
        </div>
        <DataTable
          rows={draft?.serviceRates ?? []}
          empty={t('rates.noServiceRates')}
          getRowKey={(row) => `${row.kind}:${row.rateKey}`}
          searchText={(row) => `${row.kind} ${row.rateKey} ${row.unitName}`}
          defaultSort={{ key: 'kind', dir: 'asc' }}
          columns={[
            {
              key: 'kind',
              header: t('rates.kind'),
              sortValue: (row) => row.kind,
              render: (row) => (
                <select
                  value={row.kind}
                  onChange={(event) => {
                    const kind = event.target.value as ServiceUsageRateKind;
                    updateRow(row, { kind, unitName: UNITS[kind] });
                  }}
                >
                  {KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {t(`rates.kinds.${kind}`)}
                    </option>
                  ))}
                </select>
              ),
            },
            {
              key: 'rateKey',
              header: t('rates.rateKey'),
              sortValue: (row) => row.rateKey,
              render: (row) => (
                <input
                  value={row.rateKey}
                  onChange={(event) => updateRow(row, { rateKey: event.target.value })}
                />
              ),
            },
            { key: 'unit', header: t('rates.unit'), render: (row) => row.unitName },
            {
              key: 'cost',
              header: t('rates.costMicroUsd'),
              render: (row) => (
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={row.costMicroUsdPerUnit}
                  onChange={(event) => updateRow(row, { costMicroUsdPerUnit: event.target.value })}
                />
              ),
            },
            {
              key: 'margin',
              header: t('rates.margin'),
              render: (row) => (
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={row.marginMultiplier}
                  onChange={(event) => updateRow(row, { marginMultiplier: event.target.value })}
                />
              ),
            },
            {
              key: 'provisional',
              header: t('rates.provisional'),
              render: (row) => (
                <input
                  type="checkbox"
                  checked={row.provisional}
                  onChange={(event) => updateRow(row, { provisional: event.target.checked })}
                />
              ),
            },
            {
              key: 'evidence',
              header: t('rates.evidence'),
              render: (row) => (
                <div className="rate-evidence-fields">
                  <input
                    type="url"
                    placeholder={t('rates.sourceUrl')}
                    value={row.sourceUrl}
                    onChange={(event) => updateRow(row, { sourceUrl: event.target.value })}
                  />
                  <input
                    placeholder={t('rates.verifiedAt')}
                    value={row.verifiedAt}
                    onChange={(event) => updateRow(row, { verifiedAt: event.target.value })}
                  />
                </div>
              ),
            },
            {
              key: 'actions',
              header: t('common.actions'),
              render: (row) => (
                <button
                  type="button"
                  className="secondary danger"
                  onClick={() => removeRow(row)}
                  disabled={busy}
                >
                  {t('common.delete')}
                </button>
              ),
            },
          ]}
        />
        {draftState?.error ? (
          <p className="error" role="alert">
            {draftState.error}
          </p>
        ) : null}
      </section>

      <section className="panel form-grid rate-usd-card">
        <h2>{t('rates.usdPerCredit')}</h2>
        <p className="warning">{t('rates.usdPerCreditWarning')}</p>
        <label>
          {t('rates.usdPerCreditValue')}
          <input
            type="number"
            min="0"
            step="any"
            value={draft?.usdPerCredit?.value ?? ''}
            onChange={(event) =>
              setDraft((value) =>
                value?.usdPerCredit
                  ? { ...value, usdPerCredit: { ...value.usdPerCredit, value: event.target.value } }
                  : value
              )
            }
          />
        </label>
        <label>
          {t('rates.sourceUrl')}
          <input
            type="url"
            value={draft?.usdPerCredit?.sourceUrl ?? ''}
            onChange={(event) =>
              setDraft((value) =>
                value?.usdPerCredit
                  ? {
                      ...value,
                      usdPerCredit: { ...value.usdPerCredit, sourceUrl: event.target.value },
                    }
                  : value
              )
            }
          />
        </label>
        <label>
          {t('rates.verifiedAt')}
          <input
            value={draft?.usdPerCredit?.verifiedAt ?? ''}
            onChange={(event) =>
              setDraft((value) =>
                value?.usdPerCredit
                  ? {
                      ...value,
                      usdPerCredit: { ...value.usdPerCredit, verifiedAt: event.target.value },
                    }
                  : value
              )
            }
          />
        </label>
        {draft?.usdPerCredit && !validateUsdPerCredit(draft.usdPerCredit).ok ? (
          <p className="error" role="alert">
            {t('rates.usdPerCreditInvalid')}
          </p>
        ) : null}
      </section>

      <section className="panel">
        <h2>{t('rates.publishedStatus')}</h2>
        <dl className="criteria-summary">
          <div>
            <dt>{t('rates.publishedVersion')}</dt>
            <dd>
              <code>{loaded?.publishedVersion ?? t('common.none')}</code>
            </dd>
          </div>
          <div>
            <dt>{t('rates.publishedAt')}</dt>
            <dd>{loaded?.publishedEffectiveFrom ?? t('common.none')}</dd>
          </div>
          <div>
            <dt>{t('rates.modelRateCount')}</dt>
            <dd>{current?.rates.length ?? 0}</dd>
          </div>
          <div>
            <dt>{t('rates.serviceRateCount')}</dt>
            <dd>{current?.serviceRates.length ?? 0}</dd>
          </div>
        </dl>
      </section>

      <section className="panel rate-model-pricing-note">
        <div>
          <h2>{t('rates.modelPricingTitle')}</h2>
          <p>{t('rates.modelPricingBody')}</p>
        </div>
        <button type="button" className="secondary" onClick={onOpenModels}>
          {t('rates.openModels')}
        </button>
      </section>

      <section className="panel rate-save-bar">
        <div>
          <strong>
            {t('rates.changeSummary', {
              serviceRateCount: draftState?.summary?.serviceRateCount ?? 0,
              usdPerCredit: draftState?.summary?.usdPerCreditChanged
                ? t('common.changed')
                : t('common.none'),
            })}
          </strong>
          <p className="muted">{t('rates.publishNotice')}</p>
        </div>
        <button
          type="button"
          disabled={busy || !hasChanges || Boolean(draftState?.error)}
          onClick={() => void save()}
        >
          {busy ? t('rates.saving') : t('common.save')}
        </button>
      </section>
    </section>
  );
}
